import React, { useState } from "react";
import { Box, Switch, Select, MenuItem, useTheme } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// Ver TAREA: reorganización de Configuración/Opciones -- mismo patrón de
// tarjetas con ícono+título que Marketing/Analíticas/Consumo de WhatsApp.
// Puramente presentacional: NINGÚN componente de acá conoce endpoints ni
// tiene su propio estado de guardado -- el caller (Options.js) sigue
// invocando exactamente el mismo handleX(...) de siempre; esto solo
// cambia cómo se ve el control (switch en vez de <Select> de 2 opciones,
// seg-select compacto en vez de dropdown ancho para los de más opciones).

export const SectionCard = ({ icon, title, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const borderColor = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
  const dividerColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: 1.25 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: "7px",
          background: isDark ? "rgba(3,153,255,0.14)" : "rgba(3,153,255,0.10)",
          color: "#0399FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 15 } })}
        </Box>
        <Box sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>{title}</Box>
      </Box>
      <Box sx={{
        borderRadius: "12px", border: `0.5px solid ${borderColor}`,
        background: theme.palette.background.paper, overflow: "hidden",
        // El divisor entre filas vive ACÁ (selector del hijo directo), no en
        // cada fila -- así una fila condicional (ej. los TextField de LGPD
        // que solo aparecen si enableLGPD==="enabled") nunca deja un borde
        // huérfano al final; el ":last-child" siempre encuentra al que
        // esté rendereado de verdad en ese momento.
        "& > *": { padding: "13px 14px", borderBottom: `0.5px solid ${dividerColor}` },
        "& > *:last-child": { borderBottom: "none" },
      }}>
        {children}
      </Box>
    </Box>
  );
};

// Fila compacta label+control (toggles y seg-selects).
export const OptionRow = ({ label, sub, control }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}>{label}</Box>
      {sub && <Box sx={{ fontSize: 11, color: "text.secondary", mt: "2px" }}>{sub}</Box>}
    </Box>
    <Box sx={{ flexShrink: 0 }}>{control}</Box>
  </Box>
);

// Fila de ancho completo para un TextField (mensajes personalizados, LGPD,
// pagos) -- mismo padding/divisor que OptionRow, heredado del SectionCard.
export const FieldRow = ({ children }) => (
  <Box sx={{ width: "100%" }}>{children}</Box>
);

// Adaptador puro boolean -- el caller traduce boolean<->"enabled"/"disabled"
// (o boolean real) en la línea del onChange, nunca acá.
export const ToggleField = ({ checked, onChange, loading }) => (
  <Switch
    checked={!!checked}
    disabled={loading}
    onChange={(e) => onChange(e.target.checked)}
    sx={{
      "& .MuiSwitch-switchBase.Mui-checked": { color: "#0399FF" },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#0399FF", opacity: 0.5 },
    }}
  />
);

// Toggle de 2 chips lado a lado -- para campos Sí/No que el mockup de
// Empresas pide como chip en vez de switch (Activo, Generar Factura).
// Puro adaptador de valor: el caller decide qué value real manda cada
// chip (booleanos, strings, lo que sea) vía `options`.
export const ChipToggle2 = ({ value, onChange, options, loading }) => (
  <Box sx={{ display: "flex", gap: "6px" }}>
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <Box
          key={String(opt.value)}
          onClick={() => !loading && onChange(opt.value)}
          sx={{
            flex: 1, textAlign: "center", padding: "9px", borderRadius: "8px",
            fontSize: 12, fontWeight: 700, cursor: loading ? "default" : "pointer",
            border: "0.5px solid", borderColor: selected ? "primary.main" : "divider",
            bgcolor: selected ? "primary.main" : "action.hover",
            color: selected ? "#fff" : "text.secondary",
            opacity: loading ? 0.6 : 1,
          }}
        >
          {opt.label}
        </Box>
      );
    })}
  </Box>
);

// Mismo <Select> de MUI de siempre, solo restyled compacto -- para campos
// con más de 2 opciones (Programación de Horario, Tipo de Bot).
export const SegSelectField = ({ value, onChange, options, loading }) => (
  <Select
    size="small"
    value={value}
    disabled={loading}
    onChange={(e) => onChange(e.target.value)}
    sx={{
      fontSize: 12.5, fontWeight: 600, borderRadius: "8px", minWidth: 150,
      "& .MuiSelect-select": { padding: "6px 10px" },
    }}
  >
    {options.map(opt => (
      <MenuItem key={String(opt.value)} value={opt.value} sx={{ fontSize: 13 }}>{opt.label}</MenuItem>
    ))}
  </Select>
);

// Candado + chevron -- el gate de isSuper() vive en el CALLER (Options.js
// decide si renderiza este componente o no), acá adentro no hay ningún
// chequeo de permisos porque este componente nunca debería montarse para
// alguien que no sea superadmin.
//
// Ver TAREA: reorganización de Departamentos/Colas (mockup
// https://claude.ai/artifact/K8LZQA5tK1kobraWH9JVp6) -- prop `icon`
// opcional, retrocompatible: si no se pasa, sigue el candado ámbar de
// siempre (Opciones no cambia); si se pasa, reemplaza el candado por
// ese ícono para tarjetas colapsables que NO son superadmin-only (ej.
// "Configuración avanzada"/"Menú del chatbot" en QueueModal).
export const CollapsibleLockedCard = ({ title, sub, children, icon, extra }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [expanded, setExpanded] = useState(false);
  return (
    <Box>
      <Box
        onClick={() => setExpanded(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpanded(v => !v); }}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
          borderRadius: "12px", border: `0.5px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`,
          background: theme.palette.background.paper, padding: "13px 14px", cursor: "pointer",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          {icon
            ? React.cloneElement(icon, { sx: { fontSize: 15, color: "#0399FF", flexShrink: 0 } })
            : <LockOutlinedIcon sx={{ fontSize: 15, color: "#f59e0b", flexShrink: 0 }} />}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>{title}</Box>
            {sub && <Box sx={{ fontSize: 11, color: "text.secondary", mt: "2px" }}>{sub}</Box>}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
          {extra}
          <ExpandMoreRoundedIcon sx={{
            fontSize: 20, color: "text.secondary",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s",
          }} />
        </Box>
      </Box>
      {expanded && <Box sx={{ mt: 1.5 }}>{children}</Box>}
    </Box>
  );
};
